import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '@/modules/auth'
import { useTripStore } from '@/stores'
import { FeedApi, type FeedResponse } from '@/shared/api'

/**
 * Loads the agentic Ranger feed. Mirrors useDashboard's lifecycle: loads once on
 * mount and refreshes when the trip store changes (a trip mutation invalidates the
 * backend's cached recommendations).
 */
export function useFeed() {
  const { getAccessToken, isAuthenticated } = useAuth()
  const [feed, setFeed] = useState<FeedResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const hasLoadedRef = useRef(false)

  const feedApi = useMemo(() => new FeedApi(getAccessToken), [getAccessToken])
  const trips = useTripStore((state) => state.trips || [])

  const loadFeed = useCallback(async () => {
    if (!isAuthenticated) return
    setIsLoading(true)
    setError(null)
    try {
      const data = await feedApi.getFeed()
      setFeed(data)
      hasLoadedRef.current = true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load your feed')
    } finally {
      setIsLoading(false)
    }
  }, [feedApi, isAuthenticated])

  useEffect(() => {
    if (isAuthenticated && !hasLoadedRef.current) {
      loadFeed()
    }
  }, [isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (trips.length > 0 && hasLoadedRef.current) {
      loadFeed()
    }
  }, [trips.length]) // eslint-disable-line react-hooks/exhaustive-deps

  const refreshFeed = useCallback(() => {
    if (isAuthenticated) loadFeed()
  }, [isAuthenticated]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    feed,
    cards: feed?.cards ?? [],
    tier: feed?.tier ?? 'free',
    aiEnabled: feed?.aiEnabled ?? false,
    isLoading,
    error,
    refreshFeed,
  }
}
